"use strict";

/**
 * <cart-checkout>
 * Reliability + progression feedback for the cart Checkout CTA.
 *
 * Goal: reduce checkout friction from the Terms & Conditions gate while keeping
 * the requirement enforced. Behaviour:
 *
 *  - The Checkout button shows a VISUAL disabled state (aria-disabled + dimmed)
 *    until every prerequisite (the required terms checkbox) is satisfied; it
 *    visually enables the moment the box is checked. We never set the native
 *    `disabled` attribute on the submit button, because that would drop the
 *    required `checkout` parameter from the form submission.
 *  - If the customer clicks while still gated, we DO NOT silently do nothing:
 *    we surface an inline error, dim->highlight the box, and auto-scroll/focus
 *    to it.
 *  - When prerequisites pass we submit the cart form ourselves in the capture
 *    phase via requestSubmit()/submit(), so a stray third-party T&C app cannot
 *    swallow the submit or leave the button dead.
 *  - We defensively clear states a blocker app may leave on the button
 *    (disabled attribute, pointer-events:none) so the CTA reliably redirects.
 *  - The button can never get stuck in the spinner/locked state (failsafe).
 */
(function () {
  if (customElements.get("cart-checkout")) {
    return;
  }

  function ensureStyles() {
    if (document.getElementById("pe-cart-checkout-styles")) {
      return;
    }
    var s = document.createElement("style");
    s.id = "pe-cart-checkout-styles";
    s.textContent =
      ".cart-ctas{position:relative;z-index:2;isolation:isolate}" +
      ".cart-checkout-button{position:relative;z-index:2;pointer-events:auto!important;touch-action:manipulation}" +
      ".cart-checkout-button[aria-disabled=\"true\"]{opacity:0.45;cursor:not-allowed}" +
      ".cart-dynamic-checkout-buttons{position:relative;z-index:1}";
    (document.head || document.documentElement).appendChild(s);
  }

  class CartCheckout extends HTMLElement {
    connectedCallback() {
      ensureStyles();

      this.form = this.closest("form") || document.getElementById("cart");
      this.button =
        this.querySelector("[data-checkout-button]") || document.getElementById("checkout");

      if (!this.form || !this.button || this.initialized) {
        return;
      }

      this.initialized = true;
      this.submitting = false;

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onInvalid = this.onInvalid.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.syncEnabledState = this.syncEnabledState.bind(this);

      // Own the click in the CAPTURE phase so we run before any third-party
      // click handler that might try to cancel/stop it.
      this.button.addEventListener("click", this.onClick, true);
      this.form.addEventListener("submit", this.onSubmit);
      window.addEventListener("pageshow", this.onPageShow);

      // Keep the button live even if an app tries to disable / block it.
      this.keepAlive();
      this._aliveTimer = window.setInterval(() => this.keepAlive(), 750);

      this.getPrerequisites().forEach((field) => {
        field.addEventListener("invalid", this.onInvalid);
        field.addEventListener("change", () => {
          // FNY 2026-10-27: validity.valid (checkValidity fires "invalid" and showed the error before any click).
          if (!field.validity || field.validity.valid) {
            this.clearFieldError(field);
          }
          this.syncEnabledState();
        });
        field.addEventListener("input", this.syncEnabledState);
      });

      // Reflect the initial gate state (button starts dimmed if a box is unchecked).
      this.syncEnabledState();
    }

    disconnectedCallback() {
      this.button?.removeEventListener("click", this.onClick, true);
      this.form?.removeEventListener("submit", this.onSubmit);
      window.removeEventListener("pageshow", this.onPageShow);
      window.clearInterval(this._aliveTimer);
    }

    /**
     * Undo the common ways a third-party script can neutralize the button.
     */
    keepAlive() {
      if (!this.button) return;
      if (this.button.disabled && this.form && this.form.querySelector) {
        // Only force-enable when the cart is not actually empty.
        var hasItems = !this.classList.contains("is-empty") &&
          !document.querySelector("#main-cart-footer.is-empty");
        if (hasItems) {
          this.button.disabled = false;
        }
      }
      var st = this.button.style;
      if (st && st.pointerEvents === "none") {
        st.pointerEvents = "auto";
      }
    }

    getPrerequisites() {
      const fields = new Set();
      this.querySelectorAll("[required], [data-checkout-prerequisite]").forEach((el) =>
        fields.add(el),
      );
      if (this.form && this.form.elements) {
        Array.from(this.form.elements).forEach((el) => {
          if (el.required || el.hasAttribute("data-checkout-prerequisite")) {
            fields.add(el);
          }
        });
      }
      return Array.from(fields);
    }

    /** True when every prerequisite is currently satisfied. */
    allPrerequisitesValid() {
      return this.getPrerequisites().every((field) =>
        // FNY 2026-10-27: read validity WITHOUT firing "invalid" (checkValidity showed the error on page load).
        field.validity ? field.validity.valid : (typeof field.checkValidity === "function" ? field.checkValidity() : true),
      );
    }

    /**
     * Reflect the gate in the button's VISUAL state without disabling it for
     * real (so the submit `checkout` param is preserved and the click handler
     * can still surface guidance).
     */
    syncEnabledState() {
      if (!this.button) return;
      if (this.submitting) return;
      var hasItems = !this.classList.contains("is-empty") &&
        !document.querySelector("#main-cart-footer.is-empty");
      if (!hasItems) return;
      if (this.allPrerequisitesValid()) {
        this.button.setAttribute("aria-disabled", "false");
      } else {
        this.button.setAttribute("aria-disabled", "true");
      }
    }

    /** Our own prerequisite gate (theme terms checkbox). */
    prerequisitesPass() {
      var ok = true;
      var firstBad = null;
      this.getPrerequisites().forEach((field) => {
        if (typeof field.checkValidity === "function" && !field.checkValidity()) {
          ok = false;
          if (!firstBad) firstBad = field;
          this.showFieldError(field);
        }
      });
      if (!ok && firstBad) {
        try { firstBad.focus({ preventScroll: true }); } catch (e) { firstBad.focus(); }
        // FNY 2026-10-27: only scroll when the box is off-screen (scrolling closed the header mini-cart dropdown and hid the error).
        var fnyRect = firstBad.getBoundingClientRect();
        var fnyOffscreen = fnyRect.top < 0 || fnyRect.bottom > window.innerHeight;
        if (fnyOffscreen && typeof firstBad.scrollIntoView === "function") {
          firstBad.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return ok;
    }

    /**
     * Authoritative click handler. Runs in capture phase, so even if a
     * third-party script later calls preventDefault on the bubbling click,
     * we have already taken over and submitted the form ourselves.
     */
    onClick(event) {
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      // Gate on our own prerequisites; if they fail, stop and surface.
      if (!this.prerequisitesPass()) {
        event.preventDefault();
        event.stopPropagation();
        this.syncEnabledState();
        return;
      }
      // Prerequisites pass -> submit the cart form ourselves, bypassing any
      // third-party handler that might try to cancel the default submit.
      event.preventDefault();
      event.stopPropagation();
      this.submitCart();
    }

    submitCart() {
      if (this.submitting) return;
      this.setLoading(true);
      var form = this.form;
      // Ensure the required `checkout` param is present even though we are
      // submitting programmatically (the named button is not auto-included).
      try {
        if (!form.querySelector('input[name="checkout"]')) {
          var hidden = document.createElement("input");
          hidden.type = "hidden";
          hidden.name = "checkout";
          hidden.value = "";
          form.appendChild(hidden);
        }
      } catch (e) {}
      try {
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit();
        } else {
          form.submit();
        }
      } catch (e) {
        try { form.submit(); } catch (e2) {}
      }
    }

    onSubmit(event) {
      if (this.submitting) {
        // Allow our own programmatic submit through.
        return;
      }
      // A native submit (e.g. Enter key) — run the same gate.
      if (!this.prerequisitesPass()) {
        event.preventDefault();
        this.syncEnabledState();
        return;
      }
      this.setLoading(true);
    }

    onInvalid(event) {
      const field = event.target;
      if (!this.getErrorElement(field)) {
        return;
      }
      event.preventDefault();
      this.showFieldError(field);
    }

    getErrorElement(field) {
      if (field.id) {
        const byFor = document.querySelector(`[data-error-for="${field.id}"]`);
        if (byFor) {
          return byFor;
        }
      }
      const describedBy = field.getAttribute("aria-describedby");
      if (!describedBy) return null;
      // aria-describedby may list multiple ids (helper + error); pick the error node.
      var ids = describedBy.split(/\s+/);
      for (var i = 0; i < ids.length; i++) {
        var el = document.getElementById(ids[i]);
        if (el && el.classList.contains("cart-checkout-prerequisite-error")) {
          return el;
        }
      }
      return ids.length ? document.getElementById(ids[ids.length - 1]) : null;
    }

    showFieldError(field) {
      const target = this.getErrorElement(field);
      const message =
        field.getAttribute("data-error-message") ||
        field.validationMessage ||
        "This field is required to continue to checkout.";
      if (target) {
        target.textContent = message;
        target.hidden = false;
      }
      field.setAttribute("aria-invalid", "true");
      const wrapper = field.closest("label") || field.parentElement;
      wrapper?.classList.add("cart-checkout-prerequisite--invalid");
    }

    clearFieldError(field) {
      const target = this.getErrorElement(field);
      if (target) {
        target.textContent = "";
        target.hidden = true;
      }
      field.removeAttribute("aria-invalid");
      const wrapper = field.closest("label") || field.parentElement;
      wrapper?.classList.remove("cart-checkout-prerequisite--invalid");
    }

    setLoading(isLoading) {
      this.submitting = isLoading;
      this.button.classList.toggle("cart-checkout-button--loading", isLoading);
      this.button.setAttribute("aria-busy", isLoading ? "true" : "false");
      window.clearTimeout(this._stuckTimer);
      if (isLoading) {
        this._stuckTimer = window.setTimeout(() => this.reset(), 6000);
      }
    }

    reset() {
      this.setLoading(false);
      this.syncEnabledState();
    }

    onPageShow(event) {
      if (event.persisted) {
        this.reset();
      }
    }
  }

  customElements.define("cart-checkout", CartCheckout);
})();
